class EmailPopup {
  constructor() {
    this.popup = document.getElementById('email-popup');
    if (!this.popup) return;

    this.backdrop = document.getElementById('email-popup-backdrop');
    this.closeBtn = document.getElementById('email-popup-close');
    this.input = document.getElementById('email-popup-input');
    this.submitBtn = document.getElementById('email-popup-submit');
    this.errorEl = document.getElementById('email-popup-error');
    this.formState = document.getElementById('email-popup-form-state');
    this.successState = document.getElementById('email-popup-success-state');

    this.cookieName = 'email_popup_shown';
    this.cookieTTLSeconds = 86400; // 24 hours

    this.init();
  }

  init() {
    if (this.getCookie(this.cookieName)) return;
    const ageVerified = this.getCookie('age_verified');
    const userState = this.getCookie('user_state');
    if (ageVerified && userState) {
      if (this.isStateEligible(userState)) setTimeout(() => this.show(), 3000);
    } else {
      document.addEventListener('locationUpdated', (e) => {
        const state = e.detail && e.detail.state;
        if (state && this.isStateEligible(state)) setTimeout(() => this.show(), 3000);
      }, { once: true });
    }

    this.bindCloseEvents();
    this.bindSubmit();
  }

  isStateEligible(state) {
    if (!window.themeSettings) return true; // fail open if settings unavailable
    const key = `eligible_state_${state}`;
    return window.themeSettings[key] === true;
  }

  show() {
    if (this.getCookie(this.cookieName)) return;
    this.setCookie(this.cookieName, 'true', this.cookieTTLSeconds);
    this.popup.setAttribute('aria-hidden', 'false');
    this.popup.classList.add('email-popup--visible');

    // Focus the email input for accessibility
    if (this.input) {
      requestAnimationFrame(() => this.input.focus());
    }

    // Trap focus inside modal
    this.trapFocus();
  }

  hide() {
    this.popup.classList.remove('email-popup--visible');
    this.popup.setAttribute('aria-hidden', 'true');
    this.releaseFocus();
  }

  bindCloseEvents() {
    if (this.closeBtn) {
      this.closeBtn.addEventListener('click', () => this.hide());
    }
    if (this.backdrop) {
      this.backdrop.addEventListener('click', () => this.hide());
    }
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.popup.classList.contains('email-popup--visible')) {
        this.hide();
      }
    });
  }

  bindSubmit() {
    if (!this.submitBtn) return;
    this.submitBtn.addEventListener('click', () => this.handleSubmit());
    if (this.input) {
      this.input.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') this.handleSubmit();
      });
      // Clear error on input
      this.input.addEventListener('input', () => this.clearError());
    }
  }

  getKlaviyoCompanyId() {
    const script = document.querySelector('script[src*="klaviyo.js?company_id"]');
    if (!script) return null;
    try {
      const src = script.src.startsWith('//') ? 'https:' + script.src : script.src;
      return new URL(src).searchParams.get('company_id');
    } catch {
      return null;
    }
  }

  async handleSubmit() {
    const email = this.input ? this.input.value.trim() : '';

    if (!this.isValidEmail(email)) {
      this.showError('Please enter a valid email address');
      return;
    }

    this.clearError();
    this.submitBtn.disabled = true;
    this.submitBtn.textContent = '...';

    try {
      const listId = window.themeSettings && window.themeSettings.email_popup_klaviyo_list_id;
      const companyId = this.getKlaviyoCompanyId();

      if (listId && companyId) {
        await this.submitToKlaviyo(email, listId, companyId);
      } else {
        this.showError('Something went wrong, please try again');
      }
    } finally {
      this.submitBtn.disabled = false;
      if (this.submitBtn.dataset.label) {
        this.submitBtn.textContent = this.submitBtn.dataset.label;
      }
    }
  }

  async submitToKlaviyo(email, listId, companyId) {
    try {
      const response = await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${companyId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'revision': '2023-12-15'
        },
        body: JSON.stringify({
          data: {
            type: 'subscription',
            attributes: {
              custom_source: 'Email Popup',
              profile: {
                data: {
                  type: 'profile',
                  attributes: { email }
                }
              }
            },
            relationships: {
              list: {
                data: {
                  type: 'list',
                  id: listId
                }
              }
            }
          }
        })
      });

      if (response.ok || response.status === 202) {
        this.showSuccess();
      } else {
        this.showError('Something went wrong, please try again');
      }
    } catch {
      this.showError('Something went wrong, please try again');
    }
  }


  showSuccess() {
    if (this.formState) this.formState.style.display = 'none';
    if (this.successState) {
      this.successState.style.display = 'flex';
      this.successState.setAttribute('aria-hidden', 'false');
      this.successState.classList.add('visible');
    }
  }

  showError(message) {
    if (!this.errorEl) return;
    const icon = `<svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true"><mask id="mask0_4184_72417" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16"><rect width="16" height="16" fill="#D9D9D9"/></mask><g mask="url(#mask0_4184_72417)"><path d="M7.99885 11.4448C8.19462 11.4448 8.35807 11.38 8.48918 11.2504C8.62029 11.121 8.68585 10.9584 8.68585 10.7626C8.68585 10.5668 8.62068 10.4027 8.49035 10.2703C8.3599 10.1378 8.19685 10.0716 8.00118 10.0716C7.8054 10.0716 7.64196 10.1377 7.51085 10.2701C7.37973 10.4024 7.31418 10.5665 7.31418 10.7622C7.31418 10.9579 7.37935 11.1206 7.50968 11.2503C7.64012 11.3799 7.80318 11.4448 7.99885 11.4448ZM8.03535 8.74958C8.21368 8.74958 8.36312 8.68881 8.48368 8.56725C8.60435 8.44569 8.66468 8.29603 8.66468 8.11825V5.16058C8.66468 4.98281 8.60368 4.83314 8.48168 4.71158C8.35979 4.59014 8.20968 4.52942 8.03135 4.52942C7.85301 4.52942 7.70357 4.59014 7.58301 4.71158C7.46235 4.83314 7.40201 4.98281 7.40201 5.16058V8.11825C7.40201 8.29603 7.46301 8.44569 7.58501 8.56725C7.7069 8.68881 7.85701 8.74958 8.03535 8.74958ZM8.00335 14.7781C7.06512 14.7781 6.18468 14.6005 5.36201 14.2454C4.53946 13.8903 3.82129 13.4059 3.20751 12.7923C2.59385 12.1785 2.10946 11.4601 1.75435 10.6372C1.39924 9.81425 1.22168 8.93336 1.22168 7.99458C1.22168 7.05569 1.39924 6.17469 1.75435 5.35158C2.10946 4.52836 2.59374 3.81169 3.20718 3.20158C3.82062 2.59147 4.53885 2.10847 5.36185 1.75258C6.18496 1.39669 7.06601 1.21875 8.00501 1.21875C8.94401 1.21875 9.82518 1.39664 10.6485 1.75242C11.472 2.10819 12.1886 2.59103 12.7985 3.20092C13.4085 3.81069 13.8914 4.52781 14.2472 5.35225C14.6031 6.17669 14.781 7.05819 14.781 7.99675C14.781 8.93519 14.6031 9.81575 14.2472 10.6384C13.8913 11.461 13.4083 12.1783 12.7982 12.7904C12.1881 13.4025 11.471 13.8868 10.6468 14.2432C9.82274 14.5998 8.94157 14.7781 8.00335 14.7781ZM8.00551 13.5154C9.5394 13.5154 10.8412 12.9792 11.911 11.9069C12.9808 10.8345 13.5157 9.53025 13.5157 7.99425C13.5157 6.46036 12.9815 5.15853 11.913 4.08875C10.8447 3.01897 9.5399 2.48408 7.99868 2.48408C6.46768 2.48408 5.16585 3.0183 4.09318 4.08675C3.02062 5.15508 2.48435 6.45986 2.48435 8.00108C2.48435 9.53208 3.02051 10.8339 4.09285 11.9066C5.16529 12.9791 6.46951 13.5154 8.00551 13.5154Z" fill="#A30000"/></g></svg>`;
    this.errorEl.innerHTML = `${icon}${message}`;
    this.errorEl.classList.add('visible');
    if (this.input) {
      this.input.setAttribute('aria-invalid', 'true');
      this.input.focus();
    }
  }

  clearError() {
    if (!this.errorEl) return;
    this.errorEl.textContent = '';
    this.errorEl.classList.remove('visible');
    if (this.input) this.input.removeAttribute('aria-invalid');
  }

  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email);
  }

  trapFocus() {
    this._focusableEls = this.popup.querySelectorAll(
      'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabindex="-1"])'
    );
    if (this._focusableEls.length === 0) return;
    this._firstFocusable = this._focusableEls[0];
    this._lastFocusable = this._focusableEls[this._focusableEls.length - 1];
    this._focusTrapHandler = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey) {
        if (document.activeElement === this._firstFocusable) {
          e.preventDefault();
          this._lastFocusable.focus();
        }
      } else {
        if (document.activeElement === this._lastFocusable) {
          e.preventDefault();
          this._firstFocusable.focus();
        }
      }
    };
    this.popup.addEventListener('keydown', this._focusTrapHandler);
  }

  releaseFocus() {
    if (this._focusTrapHandler) {
      this.popup.removeEventListener('keydown', this._focusTrapHandler);
    }
  }

  getCookie(name) {
    const cookieName = name + '=';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      let cookie = cookies[i].trim();
      if (cookie.indexOf(cookieName) === 0) {
        return cookie.substring(cookieName.length);
      }
    }
    return '';
  }

  setCookie(name, value, seconds) {
    const expires = new Date(Date.now() + seconds * 1000).toUTCString();
    document.cookie = `${name}=${value}; expires=${expires}; path=/`;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  new EmailPopup();
});
